import React, { useMemo, useRef } from 'react';

import {
  fallbackToScientificNotation,
  getChartDropdownOptions,
  getPipelineChartLink,
  isZeroOrEmptyChartValue,
} from '../../../utils/chart_utils/chart_utils';

import useCSVChartData from '../helpers/useCSVChartData';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query';

import { ChartProvider } from '../ChartContext';
import MultiAverageDetailPane from '../details/MultiAverageDetailPane';

import {
  formatSequencedLineChartDatasets,
  getParsedChartPoint,
  getThreeDatasetLineChartDatasets,
} from '../helpers/dataset.helpers';
import { formatChartDate } from '../../../utils/date_utils';

import { ChartWrapper } from '../d3-charts/ChartWrapper';
import LineChart from '../d3-charts/line/D3LineChart';

import { useRefDimensions } from '../d3-charts/d3-helpers';

export const useInferenceLatencyData = ({
  deploymentID,
  startTimestamp,
  endTimestamp,
  step
}) => {
  const prometheusRequestObj = {
    startTimestamp,
    endTimestamp,
    step,
    transformData: data => data?.map(point => getParsedChartPoint(point[1], point[0])),
  };

  const inferenceLatency99Response = usePrometheusQuery(
    deploymentID === null
      ? null
      : `histogram_quantile(0.99, sum(rate(latency_histogram_ns_bucket{deploy_id="${deploymentID}"}[${step}s])) by (le)) / 1e6`,
    {
      ...prometheusRequestObj,
      queryKey: `inference-latency-p99-${deploymentID}`,
    },
  );
  const inferenceLatency95Response = usePrometheusQuery(
    deploymentID === null
      ? null
      : `histogram_quantile(0.95, sum(rate(latency_histogram_ns_bucket{deploy_id="${deploymentID}"}[${step}s])) by (le)) / 1e6`,
    {
      ...prometheusRequestObj,
      queryKey: `inference-latency-p95-${deploymentID}`,
    },
  );
  const inferenceLatency50Response = usePrometheusQuery(
    deploymentID === null
      ? null
      : `histogram_quantile(0.50, sum(rate(latency_histogram_ns_bucket{deploy_id="${deploymentID}"}[${step}s])) by (le)) / 1e6`,
    {
      ...prometheusRequestObj,
      queryKey: `inference-latency-p50-${deploymentID}`,
    },
  );

  const p99LatencyData = inferenceLatency99Response?.data;
  const p95LatencyData = inferenceLatency95Response?.data;
  const p50LatencyData = inferenceLatency50Response?.data;

  const transformedDatasets = useMemo(() => getThreeDatasetLineChartDatasets(p99LatencyData, p95LatencyData, p50LatencyData).map(formatSequencedLineChartDatasets), [p99LatencyData, p95LatencyData, p50LatencyData]);

  const csvDatasets = useMemo(() => {
    if (!transformedDatasets?.length) {
      return [];
    }
    return transformedDatasets?.map(dataset => ({
      ...dataset,
      data: dataset?.data?.map(point => isZeroOrEmptyChartValue(point.y) ? { y: 0, x: point.x } : point),
    }))
  }, [transformedDatasets])

  const csvData = useCSVChartData({
    chartType: 'multi',
    title: 'Inference Latency',
    data: csvDatasets,
  })

  return {
    datasets: transformedDatasets,
    csvData,
    isLoading: inferenceLatency99Response?.isLoading
      || inferenceLatency95Response?.isLoading
      || inferenceLatency50Response?.isLoading,
    message: inferenceLatency99Response?.error
      || inferenceLatency95Response?.error
      || inferenceLatency50Response?.error
      || null,
  }
}

export const InferenceLatencyChart = ({
  datasets,
  csvData,
  labelFormatString,
  isLoading,
  message,
  pipelineID,
  pipelineVersion,
  chartID,
  startDate,
  endDate,
  isEnlarged = false,
}) => {
  const chartTitle = 'Inference Latency';

  const chartLink = useMemo(() => getPipelineChartLink({
    pipelineID, pipelineVersion, chartID,
  }), [chartID, pipelineID, pipelineVersion]);

  const dropdownOptions = getChartDropdownOptions({
    chartLink,
    chartTitle,
    csvData,
    pipelineID,
    pipelineVersion,
    isEnlarged,
  })

  const containerRef = useRef();
  const dimensions = useRefDimensions(containerRef)

  return (
    <ChartProvider
      chartID={chartID}
      dropdownOptions={dropdownOptions}
      units="ms"
      datasets={datasets}
      usePaddedMax
    >
      <ChartWrapper
        containerRef={containerRef}
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        detailRenderer={MultiAverageDetailPane}
      >
        <LineChart
          chartId="inference-latency"
          datasets={datasets}
          xTickFormat={formatChartDate(labelFormatString)}
          yTickFormat={fallbackToScientificNotation}
          numberOfXTicks={6}
          numberOfYTicks={6}
          startDate={startDate}
          endDate={endDate}
          areaOpacity={0.8}
          containerHeight={dimensions?.height && dimensions.height > 0 ? dimensions.height : 0}
          containerWidth={dimensions?.width && dimensions.width > 0 ? dimensions.width : 0}
          showPoints
        />
      </ChartWrapper>
    </ChartProvider>
  )
}

const InferenceLatencyChartWithData = (props) => {
  const {
    deploymentID, startTimestamp, endTimestamp, step,
  } = props;

  const {
    datasets,
    csvData,
    isLoading,
    message,
  } = useInferenceLatencyData({
    deploymentID, startTimestamp, endTimestamp, step
  })

  return (
    <InferenceLatencyChart
      {...props}
      datasets={datasets}
      csvData={csvData}
      isLoading={isLoading}
      message={message}
      startDate={new Date(startTimestamp)}
      endDate={new Date(endTimestamp)}
    />
  )
}

export default InferenceLatencyChartWithData;
